import {
  DOSE_UNITS,
  FORM_UNITS,
  UNKNOWN_UNIT,
  formatUnitLabel,
  isMeasuredUnit,
} from '@constants/medication-dosage';

import type {
  CodeableConcept,
  Identifier,
  Ratio,
  Reference,
} from 'generated/types';

const UCUM_SYSTEM = 'http://unitsofmeasure.org';

/**
 * Id of the contained `Medication`. Matches the id the VEG-535 migration wrote
 * and the one the proxy synthesizes for legacy records, so a record built here
 * is indistinguishable from a migrated or bridged one.
 */
export const CONTAINED_MEDICATION_ID = 'med-rx';

/**
 * The denominator unit older records were given when no dose form was known.
 * No longer written — §10.1 of Medly's transform rules wants a unitless
 * `{value: 1}` instead, since a bare count invents a form the source never
 * stated — but still read, so a record that stored it seeds its Form correctly.
 */
const DEFAULT_STRENGTH_DENOMINATOR = 'each';

/**
 * The parts of a contained `Medication` this client reads and writes.
 *
 * Declared structurally rather than pulled from the generated schema for the
 * same reason the backend keeps `contained` as `Resource[]`: the FHIR server can
 * hand back any resource type in there, so narrowing is a runtime check on our
 * side, not something the schema guarantees.
 */
export interface ContainedIngredient {
  itemCodeableConcept?: CodeableConcept | null;
  itemReference?: Reference | null;
  isActive?: boolean | null;
  strength?: Ratio | null;
}

export interface ContainedMedication {
  resourceType?: string | null;
  id?: string | null;
  code?: CodeableConcept | null;
  identifier?: Identifier[] | null;
  status?: string | null;
  form?: CodeableConcept | null;
  amount?: Ratio | null;
  manufacturer?: Reference | null;
  ingredient?: ContainedIngredient[] | null;
}

/** One editable row of the strength table: what the curator types. */
export interface MedicationIngredientRow {
  /**
   * Ingredient name. Optional, and only asked for on a combination product:
   * a single-ingredient row is the product itself, so a second box repeating the
   * title says nothing. Blank means "name it after the product", which is what
   * a curator who has no ingredient breakdown to hand should leave it as.
   */
  name: string;
  strengthValue: string;
  strengthUnit: string;
  /**
   * Denominator of the strength Ratio: the "per" half. A tablet is "875 mg per
   * 1 tablet", a liquid is "250 mg per 5 mL". Blank means per one of the dose
   * form, which is the common case for solids.
   */
  strengthPerValue: string;
  strengthPerUnit: string;
  /**
   * The stored ingredient this row was hydrated from, carried through the form
   * untouched so a save can merge onto it. The form edits three fields; an
   * ingredient also carries coding, `itemReference` and `isActive` that nothing
   * in the UI can express, and rebuilding from the row alone would drop them.
   * Absent on a row the curator added.
   */
  source?: ContainedIngredient;
}

export const emptyIngredientRow = (): MedicationIngredientRow => ({
  name: '',
  strengthValue: '',
  strengthUnit: '',
  strengthPerValue: '',
  strengthPerUnit: '',
});

/**
 * A CodeableConcept's human-readable name, matching the backend's
 * `codeableConceptName`: `text` first, then the first coding that carries a
 * display. EMR-sourced concepts routinely arrive coded with no `text`.
 */
export function codeableConceptName(
  concept?: CodeableConcept | null
): string | undefined {
  const text = concept?.text?.trim();
  if (text) return text;
  return concept?.coding
    ?.map(({ display }) => display?.trim())
    .find((display): display is string => Boolean(display));
}

/**
 * Whether a row is worth writing. A row hydrated from a stored ingredient is
 * kept regardless of what it holds: dropping it would delete an ingredient the
 * form can't fully express (an `itemReference`, a drug code) just because its
 * strength was never recorded.
 */
export function ingredientRowHasContent(row: MedicationIngredientRow): boolean {
  return Boolean(row.strengthValue || row.name.trim() || row.source);
}

/**
 * Clears every row name that just repeats the product name, for seeding the
 * form.
 *
 * A row with no name of its own is saved under the product's (see
 * `mergeIngredient`), so a stored record hands back rows whose names are all the
 * title. Showing that in the ingredient input would read as a breakdown someone
 * actually recorded, and a curator correcting the title would then have to
 * correct it again on every row. Blank instead, and the fallback writes the same
 * value back — an untouched save changes nothing.
 */
export function blankProductNames(
  rows: readonly MedicationIngredientRow[],
  productName: string | null | undefined
): MedicationIngredientRow[] {
  const name = productName?.trim() ?? '';
  return rows.map((row) =>
    name && row.name.trim() === name ? { ...row, name: '' } : { ...row }
  );
}

/**
 * Whether a record says how much medicine there is at all.
 *
 * The strength and the dose are one requirement between them — a tablet's
 * strength with no dose is a real record, and so is a dose of a product whose
 * strength nobody wrote down — so the check spans both and belongs to neither.
 */
export function hasMedicineAmount(
  // Only the strength value is read, and yup hands the array in with every
  // field optional, so the narrower shape is what actually matters here.
  rows: readonly { strengthValue?: string }[] | undefined,
  doseValue: string | null | undefined
): boolean {
  return Boolean((rows ?? []).some((row) => row.strengthValue) || doseValue);
}

function toNumber(value: string): number | undefined {
  if (!value) return undefined;
  const parsed = parseFloat(value);
  // Rejects Infinity as well as NaN: it serialises as null and leaves a Quantity
  // with a unit and no value.
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Maps a stored unit onto the DOSE_UNITS value, so label casing ("mEq" where the
 * table keys "meq") keeps its UCUM code and preselects in the dropdown. An
 * unknown unit is returned unchanged rather than blanked.
 */
export function canonicalDoseUnit(raw: string | null | undefined): string {
  if (!raw) return '';
  const lower = raw.trim().toLowerCase();
  const match = DOSE_UNITS.find(
    (u) => u.value.toLowerCase() === lower || u.label.toLowerCase() === lower
  );
  return match?.value ?? raw;
}

function buildQuantity(value: number, unit: string) {
  const def = DOSE_UNITS.find((u) => u.value === unit);
  // No unit means no `unit` member, not an empty one: a strength denominator
  // with no named form is a bare `{value: 1}`, and `unit: ''` would be a claim
  // that the unit is the empty string.
  if (!unit) return { value };
  return {
    value,
    unit,
    ...(isMeasuredUnit(unit) && {
      system: UCUM_SYSTEM,
      code: def?.ucumCode || unit,
    }),
  };
}

/**
 * Picks the "per what" half of a strength Ratio.
 *
 * A strength is per unit of administration form, so the dose form is the
 * denominator: "875 mg per 1 tablet", "250 mg per 5 mL". A measured form counts
 * here — a suspension's strength really is per millilitre — which it did not
 * before VEG-760, when this had to guess from the dose unit and a measured one
 * was as likely to be a legacy strength as a real form.
 */
export function strengthDenominatorUnit(doseForm?: string): string {
  // Only a word the Form could actually have set. `unknown` is a dose unit but
  // not a dose form, and anything outside the list is a legacy dose unit or the
  // free text `Medication.form.text` carries — seeding brought both in here, and
  // "500 mg per 1 mg" with a UCUM code asserting it is worse than no unit at
  // all. `FORM_UNITS` rather than a measured/counted test, because mL has to
  // survive: "250 mg per 5 mL" is the case this exists for.
  const offered = FORM_UNITS.some((unit) => unit.value === doseForm);
  if (doseForm && doseForm !== UNKNOWN_UNIT && offered) return doseForm;
  // Blank, so the Ratio keeps both halves as `{value: 1}` with no unit. Naming
  // it "each" asserted a dose form the source never stated.
  return '';
}

/**
 * The dose form a stored record implies, for seeding the Form dropdown.
 *
 * The form is now the single source of the noun on the medication form, but
 * older records never had a control for it: they carry the same word on
 * `dosage.doseQuantity.unit`, or on a strength denominator, or nowhere. Reading
 * those in order means an existing record opens with its form already filled in
 * rather than blank, which would otherwise read as "no form recorded" and get
 * saved that way.
 */
export function inferDoseForm(
  doseForm: string | null | undefined,
  doseUnit: string | null | undefined,
  rows: readonly MedicationIngredientRow[] | undefined
): string {
  // The dose the record actually stores wins. `Medication.form.text` is last
  // because it is free text nobody validated: EMR sync and OCR put "suspension",
  // "cream", "patch" in it, and seeding from that would then rewrite a stored
  // "250 mg per 5 mL" as "per 5 suspension" the moment anyone saved.
  if (doseUnit) return canonicalDoseUnit(doseUnit);
  // `each` is the synthesised denominator, not something anyone chose.
  const per = rows?.find(
    (row) =>
      row.strengthPerUnit &&
      row.strengthPerUnit !== DEFAULT_STRENGTH_DENOMINATOR
  )?.strengthPerUnit;
  if (per) return canonicalDoseUnit(per);
  return canonicalDoseUnit(doseForm);
}

/**
 * The contained `Medication` a `medicationReference` points at.
 *
 * Prefers the id the reference names, matching the backend's
 * `containedMedication`, so a statement carrying more than one can't silently
 * resolve against the wrong one.
 *
 * Deliberately more forgiving than the backend on the miss: it returns
 * undefined, this falls back to the first Medication. A statement carries
 * exactly one in practice, so a reference matching nothing means the ids drifted
 * — and resolving nothing here is worse than a stale name, because the save path
 * would treat the record as having no stored product and rebuild it from form
 * values, dropping whatever else the Medication carried.
 */
export function containedMedication(
  contained: readonly ContainedMedication[] | null | undefined,
  reference?: string | null
): ContainedMedication | undefined {
  const medications = contained?.filter(
    ({ resourceType }) => resourceType === 'Medication'
  );
  if (!medications?.length) return undefined;
  const id = reference?.startsWith('#') ? reference.slice(1) : undefined;
  if (!id) return medications[0];
  return (
    medications.find((medication) => medication.id === id) ?? medications[0]
  );
}

/**
 * Hydrate editable rows from a contained `Medication`'s `ingredient[]`. Each row
 * keeps its stored ingredient in `source` so a later save can merge onto it
 * rather than replace it.
 */
export function containedToIngredientRows(
  contained: readonly ContainedMedication[] | null | undefined,
  reference?: string | null
): MedicationIngredientRow[] {
  const ingredients = containedMedication(contained, reference)?.ingredient;
  if (!ingredients?.length) return [];
  return ingredients.map((ingredient) => ({
    name: codeableConceptName(ingredient.itemCodeableConcept) ?? '',
    strengthValue: ingredient.strength?.numerator?.value?.toString() ?? '',
    strengthUnit: canonicalDoseUnit(ingredient.strength?.numerator?.unit),
    strengthPerValue: ingredient.strength?.denominator?.value?.toString() ?? '',
    strengthPerUnit: canonicalDoseUnit(ingredient.strength?.denominator?.unit),
    source: ingredient,
  }));
}

/** Merge a row's editable strength onto the ingredient it came from. */
function mergeIngredient(
  row: MedicationIngredientRow,
  productName: string,
  denominatorUnit: string
): ContainedIngredient {
  const merged: ContainedIngredient = { ...row.source };

  // The row's own name if it has one, the product name if it doesn't. The
  // ingredient input only appears on a combination product, and even there
  // curators often don't have the breakdown to hand — where they do know it, it
  // tends to be in the medication name already. So a blank row is named after
  // the product rather than left nameless, and a two-part tablet nobody could
  // break down carries the same name twice, told apart by its strengths.
  //
  // Skipped for an ingredient identified only by `itemReference`: item[x] is a
  // choice type, so naming it would mean deleting the reference — and that
  // reference is the only thing identifying the ingredient. The product name is
  // already on the statement, so it isn't worth the trade.
  //
  // Skipped too when there is no product name, which only happens on a
  // half-built record since the title is required: whatever the ingredient
  // already carried is left alone rather than cleared.
  const referenceOnly = Boolean(
    row.source?.itemReference && !row.source.itemCodeableConcept
  );
  const name = row.name.trim() || productName;
  if (name && !referenceOnly) {
    // An EMR record's coding survives the edit only while it still names the
    // same product. A coding identifies one specific drug, so carrying it onto
    // a renamed record would leave the code and the text describing different
    // things — and the code is what Medly reads. Compared against the name the
    // concept resolved to, not its raw `text`, since a coded concept often has
    // no text and resolves through its coding display.
    const storedName = codeableConceptName(row.source?.itemCodeableConcept);
    merged.itemCodeableConcept =
      storedName === name
        ? { ...row.source?.itemCodeableConcept, text: name }
        : { text: name };
    // item[x] is a choice type. Writing the concept has to drop the reference, or
    // the ingredient carries both members and is invalid.
    delete merged.itemReference;
  }

  // Default true for a row the curator added; never overwrite a stored flag, or
  // round-tripping an excipient would promote it to an active ingredient.
  merged.isActive = row.source?.isActive ?? true;

  const strengthValue = toNumber(row.strengthValue);
  if (strengthValue !== undefined && row.strengthUnit) {
    // The denominator is the concentration's "per" half. Synthesising
    // `1 <dose form>` for it unconditionally rewrote a stored "250 mg per 5 mL"
    // as "250 mg per 1 each", a 5x misstatement on a liquid, on every save.
    const rawPer = toNumber(row.strengthPerValue);
    const perValue = rawPer !== undefined && rawPer > 0 ? rawPer : 1;
    const perUnit = row.strengthPerUnit || denominatorUnit;
    merged.strength = {
      numerator: buildQuantity(strengthValue, row.strengthUnit),
      denominator: buildQuantity(perValue, perUnit),
    };
  } else {
    // Clearing the strength in the form has to clear it in the resource too.
    delete merged.strength;
  }

  return merged;
}

/**
 * Build the contained `Medication` for a statement, merging onto `existing` when
 * the record already carries one.
 *
 * `code.text` is the product name — the same string mirrored into
 * `medicationReference.display`, since the reference is a denormalized copy.
 * Everything else on a stored `Medication` (`code.coding`, `form`, `amount`,
 * `identifier`, `manufacturer`, ...) is carried through untouched: the form
 * cannot express those, and rebuilding from form values alone would drop a drug
 * code on every save. The backend's read shim deliberately preserves legacy
 * coding, so discarding it here would undo that on the first edit.
 *
 * Empty rows are dropped, so a single-ingredient product whose strength was
 * never captured produces a Medication with a name and no `ingredient[]` rather
 * than an empty row.
 */
export function ingredientRowsToContained(
  productName: string,
  rows: MedicationIngredientRow[] | undefined,
  doseForm?: string,
  existing?: ContainedMedication
): ContainedMedication | undefined {
  const name = productName.trim();
  const denominatorUnit = strengthDenominatorUnit(doseForm);
  const ingredient = (rows ?? [])
    .filter(ingredientRowHasContent)
    .map((row) => mergeIngredient(row, name, denominatorUnit));

  if (!name && !ingredient.length) return undefined;

  const merged: ContainedMedication = {
    ...existing,
    resourceType: 'Medication',
    id: existing?.id ?? CONTAINED_MEDICATION_ID,
  };

  // Same rule as an ingredient's concept: the coding goes with the name it
  // identified, and a rename drops it rather than mislabelling a drug code.
  if (name) {
    const storedName = codeableConceptName(existing?.code);
    merged.code =
      storedName === name ? { ...existing?.code, text: name } : { text: name };
  }
  if (ingredient.length) merged.ingredient = ingredient;
  else delete merged.ingredient;

  // Dose form ("tablet", "capsule") belongs to the product, not the dosage, and
  // only a counted form is one: `unknown` is a dose unit, and a measured unit is
  // a strength. Neither is a form, and this field leaves the repo — the Medly
  // apps read the contained `Medication` through medly-backend's passthrough,
  // where a `form.text` of "mg" renders as the product's dose form.
  const form = doseForm?.trim() ?? '';
  const namesAForm =
    Boolean(form) && form !== UNKNOWN_UNIT && !isMeasuredUnit(form);
  if (namesAForm) {
    // Only when it says something new. A stored "Tablet" canonicalises to
    // "tablet", so writing unconditionally dirtied the field on every save.
    const stored = existing?.form?.text?.trim();
    if (!stored || canonicalDoseUnit(stored) !== form) {
      merged.form = { ...existing?.form, text: form };
    }
  } else if (existing?.form) {
    // Anything that isn't a named form clears it, not just a blank field. A
    // stored "Tablet" sitting beside an mL quantity is a worse answer than no
    // form at all: an absent form is only silence, where a stale one is wrong,
    // and it leaves for the Medly apps either way.
    delete merged.form;
  }

  return merged;
}

/**
 * Canonical strength string for a product: `875/125 mg`.
 *
 * The shared-unit form is the one clinicians read, so it is used whenever every
 * ingredient reports the same unit. Mixed units can't collapse that way and
 * fall back to spelling each one out (`875 mg / 5 mL`). A single ingredient
 * reads as plain `875 mg`.
 */
export function formatStrengths(
  rows: MedicationIngredientRow[] | undefined
): string {
  const strengths = (rows ?? []).filter(
    (row) => toNumber(row.strengthValue) !== undefined && row.strengthUnit
  );
  if (!strengths.length) return '';

  const units = new Set(strengths.map((row) => row.strengthUnit));
  const label = (row: MedicationIngredientRow) =>
    formatUnitLabel(row.strengthUnit, toNumber(row.strengthValue));
  const base =
    units.size === 1
      ? `${strengths.map((row) => row.strengthValue).join('/')} ${label(strengths[0])}`
      : strengths
          .map((row) => `${row.strengthValue} ${label(row)}`)
          .join(' / ');

  // A concentration is only worth spelling out when it isn't "per one": "250 mg
  // per 5 mL" matters, "875 mg per 1 tablet" is just the strength.
  const per = strengths[0];
  const perValue = toNumber(per.strengthPerValue);
  if (perValue !== undefined && perValue !== 1 && per.strengthPerUnit) {
    return `${base} per ${perValue} ${formatUnitLabel(per.strengthPerUnit, perValue)}`;
  }
  return base;
}

/**
 * Product label for display: name followed by its canonical strength.
 * Either half may be missing.
 */
export function formatProduct(
  name: string | null | undefined,
  rows: MedicationIngredientRow[] | undefined
): string {
  return [name?.trim(), formatStrengths(rows)].filter(Boolean).join(' ');
}
